import moment, { Moment } from 'moment'
import React, { useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { Flex } from '../../../App/Components'
import {
  Dashboard,
  DashBoardHeading,
  DashBoardItem,
  DashBoardNews,
} from '../../../App/Components/Container/DashBoard/Dashboard'
import { useAppDispatch, useAppSelector } from '../../../App/Hooks/reduxHooks'
import { activeEstablishmentSelector } from '../../../Establishment/Store/reducer'
import { IUser } from '../../../User/DataStore/IUser'
import { IPerformanceStats } from '../Client/IPerformanceStats'
import {
  PerformanceCard,
  PerformanceShoutOut,
  SummaryCard,
} from '../Components'
import { getPerformanceStats } from '../Store/Action'

import { FilterButton, FilterButtonContainer, PageHeading } from './style'

export const PerformanceOverview = () => {
  const dispatch = useAppDispatch()
  const [dateRange, setDateRange] = useState<'day' | 'week' | 'month'>('week')
  const [fromDate, setFromDate] = useState<Moment>(moment().startOf(dateRange))
  const [toDate, setToDate] = useState<Moment>(moment().endOf(dateRange))
  const { t } = useTranslation()

  const { users, performanceReports } = useAppSelector(state => {
    return {
      users: state.users.list,
      performanceReports: state.reports.performanceReports.list,
    }
  })
  const establishment = useAppSelector(activeEstablishmentSelector)

  useEffect(() => {
    dispatch(getPerformanceStats(fromDate, toDate))
  }, [fromDate, toDate])

  const changeRange = (range: 'day' | 'week' | 'month') => {
    setFromDate(moment().startOf(range))
    setToDate(moment().endOf(range))
    setDateRange(range)
  }

  const bestReport: IPerformanceStats = useMemo(() => {
    if (!performanceReports || performanceReports.length === 0) {
      return {
        personUuid: users && users.length > 0 ? users[0].uuid : '',
        loyalty: {
          chosenMinutes: 0,
          randomMinutes: 0,
          chosenCount: 0,
          randomCount: 0,
        },
      }
    }
    return performanceReports.reduce((currentBestReport, report) =>
      currentBestReport &&
      currentBestReport.loyalty.chosenCount > report.loyalty.chosenCount
        ? currentBestReport
        : report,
    )
  }, [performanceReports, users])

  const totalPicks = useMemo(() => {
    if (!performanceReports) return 0

    const performanceCount = performanceReports.map(
      report => report.loyalty.chosenCount,
    )

    return performanceCount.reduce(
      (accumulator: number, currentValue: number): number =>
        accumulator + currentValue,
      0,
    )
  }, [performanceReports])

  const bestPerson = useMemo(
    () => users.find(user => user.uuid === bestReport.personUuid),
    [bestReport],
  )

  const establishmentUsers = users.filter(
    user => user.establishmentId === establishment.id,
  )

  return (
    <Dashboard itemColumnCount={3}>
      <DashBoardHeading>
        <Flex
          container
          width={'100%'}
          alignItems={'center'}
          justifyContent={'space-between'}>
          <PageHeading>{t('Person Performance')}</PageHeading>
          <FilterButtonContainer container>
            <FilterButton
              onClick={() => changeRange('day')}
              active={dateRange === 'day'}>
              {t('Day')}
            </FilterButton>
            <FilterButton
              onClick={() => changeRange('week')}
              active={dateRange === 'week'}>
              {t('Week')}
            </FilterButton>
            <FilterButton
              onClick={() => changeRange('month')}
              active={dateRange === 'month'}>
              {t('Month')}
            </FilterButton>
          </FilterButtonContainer>
        </Flex>
      </DashBoardHeading>
      <DashBoardNews>
        <SummaryCard totalPicks={totalPicks} from={fromDate} to={toDate} />
      </DashBoardNews>
      <DashBoardNews>
        {bestPerson ? (
          <PerformanceShoutOut
            personName={bestPerson.firstName}
            count={bestReport.loyalty.chosenCount}
            minutes={bestReport.loyalty.chosenMinutes}
          />
        ) : null}
      </DashBoardNews>
      {performanceReports && (
        <>
          {performanceReports.map(report => {
            const personOfReport = establishmentUsers.find(
              person => person.uuid === report.personUuid,
            )

            if (!personOfReport) {
              return null
            }

            return (
              <DashBoardItem key={`report${report.personUuid}`}>
                <PerformanceCard
                  profilePicture={personOfReport.avatar}
                  lastName={personOfReport.lastName}
                  firstName={personOfReport.firstName}
                  personPerformanceData={report}
                />
              </DashBoardItem>
            )
          })}
        </>
      )}
    </Dashboard>
  )
}
